import Api from "./tumourApi";
import { combineNationalDataForBarCharts, calculateTopEightBarCharts, combineFilters } from "./modulesCommon";
import { combineAndCommit, combineRootPropertiesLists, clone } from "../helpers";

// TYPES
const NATIONAL_DATA = "NATIONAL_DATA";
const NATIONAL_DATA_LOADED = "NATIONAL_DATA_LOADED";

const DATA = "DATA";
const DATA_LOADED = "DATA_LOADED";
const DATA_LOADING = "DATA_LOADING";

const TUMOUR_LOCALISATIONS = "TUMOUR_LOCALISATIONS";
const TUMOUR_HISTOLOGIES = "TUMOUR_HISTOLOGIES";
const SURGERY_HISTOLOGIES = "SURGERY_HISTOLOGIES";
const SURGERY_PROCEDURES = "SURGERY_PROCEDURES";
const PATIENT_COMORBIDITIES = "PATIENT_COMORBIDITIES";
const SURGERY_COMPLICATIONS = "SURGERY_COMPLICATIONS";
const CLINICAL_TNMS = "CLINICAL_TNMS";
const PATHOLOGIC_TNMS = "PATHOLOGIC_TNMS";
const MR_ATTRIBUTES = "MR_ATTRIBUTES";

const emptyDataObj = {
    patientDemographics: {},
    tumourStatistics: {},
    tumourLocalisations: {},
    tumourHistologies: {},
    surgeriesStatistic: {},
    surgeryHistologies: {},
    surgeryProcedures: {},
    surgeryComplications: {},
    tumourTimeProgressStatistics: {},
    patientComorbidities: {},
    clinicalTnms: {},
    pathologicTnms: {},
    advancedQueryCriteria: {
        MrAttributes: {},
    },
    patientQuery: {},
    caseQuery: {},
};

const barChartFields = [
    {
        commitTo: PATIENT_COMORBIDITIES,
        path: ["patientComorbidities"],
        calculateTopEight: true,
    },
    {
        commitTo: TUMOUR_LOCALISATIONS,
        path: ["tumourLocalisations"],
        calculateTopEight: true,
    },
    {
        commitTo: TUMOUR_HISTOLOGIES,
        path: ["tumourHistologies"],
        calculateTopEight: true,
    },
    {
        commitTo: SURGERY_HISTOLOGIES,
        path: ["surgeryHistologies"],
        calculateTopEight: true,
    },
    {
        commitTo: SURGERY_PROCEDURES,
        path: ["surgeryProcedures"],
        calculateTopEight: true,
    },
];

const tumourDataModule = {
    namespaced: true,
    state: () => ({
        mrAttributes: Object.create({}),
        nationalData: clone(emptyDataObj),
        nationalDataLoaded: false,
        data: clone(emptyDataObj),
        dataLoaded: null,
        dataLoading: false,
    }),
    mutations: {
        [MR_ATTRIBUTES](state, mrAttributes) {
            state.mrAttributes = mrAttributes;
        },
        [NATIONAL_DATA](state, nationalData) {
            state.nationalData = nationalData;
        },
        [NATIONAL_DATA_LOADED](state, isNationalDataLoaded) {
            state.nationalDataLoaded = isNationalDataLoaded;
        },
        [DATA](state, data) {
            state.data = data;
        },
        [DATA_LOADED](state, isDataLoaded) {
            state.dataLoaded = isDataLoaded;
        },
        [DATA_LOADING](state, dataLoading) {
            state.dataLoading = dataLoading;
        },
        [TUMOUR_LOCALISATIONS](state, tumourLocalisations) {
            state.data.tumourLocalisations = tumourLocalisations;
        },
        [TUMOUR_HISTOLOGIES](state, tumourHistologies) {
            state.data.tumourHistologies = tumourHistologies;
        },
        [SURGERY_HISTOLOGIES](state, surgeryHistologies) {
            state.data.surgeryHistologies = surgeryHistologies;
        },
        [SURGERY_PROCEDURES](state, surgeryProcedures) {
            state.data.surgeryProcedures = surgeryProcedures;
        },
        [PATIENT_COMORBIDITIES](state, patientComorbidities) {
            state.data.patientComorbidities = patientComorbidities;
        },
        [CLINICAL_TNMS](state, clinicalTnms) {
            state.data.clinicalTnms = clinicalTnms;
        },
        [PATHOLOGIC_TNMS](state, pathologicTnms) {
            state.data.pathologicTnms = pathologicTnms;
        },
    },
    actions: {
        readNationalData({ commit, state, rootState }) {
            commit(NATIONAL_DATA_LOADED, false);
            let filters = combineFilters(rootState.patientQuery, rootState.caseQuery, rootState.advancedQueryCriteria);
            return Api.GetNationalData(filters, rootState).then(function (result) {
                barChartFields.forEach((element) => {
                    let statePath = state.data;
                    let resultPath = result.data;

                    for (let index = 0; index < element.path.length; index++) {
                        statePath = statePath[element.path[index]];
                        resultPath = resultPath[element.path[index]];
                    }

                    combineAndCommit(commit, element.commitTo, statePath, resultPath);
                });

                if (
                    Object.entries(state.data.surgeryComplications).length !== 0 &&
                    state.data.surgeryComplications.constructor !== Object
                )
                    if (state.data.surgeryComplications.mrAttributeRowViewModelList.length > 0)
                        commit(
                            SURGERY_COMPLICATIONS,
                            combineRootPropertiesLists(
                                state.data.surgeryComplications,
                                result.data.surgeryComplications,
                            ),
                        );

                commit(
                    PATHOLOGIC_TNMS,
                    combineTnmLists(state.data.pathologicTnms, result.data.pathologicTnmStatistics),
                );
                result.data.pathologicTnms = result.data.pathologicTnmStatistics;
                delete result.data.pathologicTnmStatistics;

                commit(CLINICAL_TNMS, combineTnmLists(state.data.clinicalTnms, result.data.clinicalTnmStatistics));
                result.data.clinicalTnms = result.data.clinicalTnmStatistics;
                delete result.data.clinicalTnmStatistics;

                commit(NATIONAL_DATA, result.data);
                commit(NATIONAL_DATA_LOADED, true);
            });
        },
        readData({ commit, state, rootState }, context) {
            let filters = combineFilters(rootState.patientQuery, rootState.caseQuery, rootState.advancedQueryCriteria);
            commit(DATA_LOADED, false);

            return Api.GetData(context, filters).then(function (result) {
                //calculate top eight properties list
                calculateTopEightBarCharts(barChartFields, result.data);

                // if context data should be combined with national data
                if (rootState.dataModule.system.isNationalDataNeeded) {
                    combineNationalDataForBarCharts(barChartFields, result.data, state.nationalData);

                    if (state.nationalData.surgeryComplications.mrAttributeRowViewModelList.length > 0)
                        result.data.surgeryComplications = combineRootPropertiesLists(
                            result.data.surgeryComplications,
                            state.nationalData.surgeryComplications,
                        );

                    result.data.pathologicTnms = combineTnmLists(
                        result.data.pathologicTnmStatistics,
                        state.nationalData.pathologicTnms,
                    );
                    delete result.data.pathologicTnmStatistics;

                    result.data.clinicalTnms = combineTnmLists(
                        result.data.clinicalTnmStatistics,
                        state.nationalData.clinicalTnms,
                    );
                    delete result.data.clinicalTnmStatistics;
                } else {
                    result.data.pathologicTnms = result.data.pathologicTnmStatistics;
                    delete result.data.pathologicTnmStatistics;

                    result.data.clinicalTnms = result.data.clinicalTnmStatistics;
                    delete result.data.clinicalTnmStatistics;
                }
                commit(DATA, result.data);
                commit(DATA_LOADED, true);
            });
        },
        // is data was loaded
        setDataLoaded({ commit }, isDataLoaded) {
            return commit(DATA_LOADED, isDataLoaded);
        },
        setDataLoading({ commit }, isDataLoading) {
            return commit(DATA_LOADING, isDataLoading);
        },
        cleanData({ commit }) {
            commit(DATA, clone(emptyDataObj));
            commit(DATA_LOADED, false);
        },
        readMrAttributes({ commit }) {
            return Api.GetMrAttributes().then((result) => {
                commit(MR_ATTRIBUTES, result.data);
            });
        },
    },
};

function combineTnmLists(contextTnms, nationalTnms) {
    if (Object.keys(contextTnms).length !== 0 && Object.keys(nationalTnms).length !== 0) {
        let combinedTItems = combineTnms(contextTnms.tItems, nationalTnms.tItems);
        let combinedNItems = combineTnms(contextTnms.nItems, nationalTnms.nItems);
        let combinedMItems = combineTnms(contextTnms.mItems, nationalTnms.mItems);

        let combined = {
            maxCountTItems: combinedTItems.maxCountItems,
            maxCountNItems: combinedNItems.maxCountItems,
            maxCountMItems: combinedMItems.maxCountItems,
            tItems: combinedTItems.tnmItems,
            nItems: combinedNItems.tnmItems,
            mItems: combinedMItems.tnmItems,
        };

        combined.tItems = sortTItems(combined.tItems);

        return combined;
    }

    if (Object.keys(contextTnms).length !== 0) return { ...contextTnms, tItems: sortTItems(contextTnms.tItems) };

    return contextTnms;
}

function sortTItems(items) {
    const sortingOrder = ["Tx", "Tis", "T0", "T1", "T2", "T3", "T4"];

    function compare(a, b) {
        const indexA = sortingOrder.indexOf(a.name);
        const indexB = sortingOrder.indexOf(b.name);

        if (indexA < indexB) return -1;

        if (indexA > indexB) return 1;

        return 0;
    }

    return items.sort(compare);
}

function combineTnms(cTnmsItems, nTnmsItems) {
    var combined = {
        maxCountItems: 0,
        tnmItems: [],
    };

    cTnmsItems.forEach(function (cTnmsItem) {
        nTnmsItems.forEach(function (nTnmsItem) {
            if (cTnmsItem.name.toLowerCase() === nTnmsItem.name.toLowerCase()) {
                var temp = {
                    name: cTnmsItem.name,
                    count: cTnmsItem.count,
                    percent: cTnmsItem.percent,
                    nationalCount: nTnmsItem.count,
                    nationalPercent: nTnmsItem.percent,
                };

                combined.tnmItems.push(temp);

                if (combined.maxCountItems < temp.percent) combined.maxCountItems = parseFloat(temp.percent);
            }
        });
    });

    return combined;
}

export default tumourDataModule;
